'use client';

import React from 'react';
import { X, Zap, TrendingUp, Bell, Download } from 'lucide-react';
import type { Tenant } from '@vm/theme';

interface HabitCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  tenant: Tenant;
}

/**
 * Habit-Creation Upgrade Modal
 *
 * Psychology: Not feature gating, but habit-creation gating
 * Triggers: When user attempts to add 3rd subscription (free tier limit = 2)
 * Message: "You're building the habit! Unlock unlimited tracking with Pro"
 */
export function HabitCreationModal({ isOpen, onClose, onUpgrade, tenant }: HabitCreationModalProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-white/10 shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 pb-4">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Close modal"
            >
              <X className="h-5 w-5 text-white/60" />
            </button>

            {/* Icon */}
            <div
              className="w-16 h-16 rounded-2xl mb-4 flex items-center justify-center"
              style={{
                background: `linear-gradient(to br, ${tenant.tokens.primary}, ${tenant.tokens.accent || tenant.tokens.primary})`
              }}
            >
              <Zap className="h-8 w-8 text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-2">
              You're Building the Habit! 🎉
            </h2>
            <p className="text-white/70 text-sm leading-relaxed">
              You've maxed out your free tier (2 subscriptions). Upgrade to Pro to track unlimited subscriptions and unlock powerful insights.
            </p>
          </div>

          {/* Pro Features */}
          <div className="px-6 py-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm">Unlimited Subscriptions</h3>
                <p className="text-white/60 text-xs">Track all your recurring payments in one place</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm">Smart Reminders</h3>
                <p className="text-white/60 text-xs">Never miss a payment with advanced notifications</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Download className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-white font-medium text-sm">Export & Analytics</h3>
                <p className="text-white/60 text-xs">CSV exports and spending insights</p>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="px-6 py-4 bg-white/5 border-t border-white/10">
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-bold text-white">$4.99</span>
              <span className="text-white/60 text-sm">/month</span>
            </div>
            <p className="text-white/60 text-xs mb-4">
              Cancel anytime. No refunds, but you'll keep access until the end of your billing period.
            </p>
          </div>

          {/* Actions */}
          <div className="p-6 pt-4 space-y-3">
            <button
              onClick={onUpgrade}
              className="w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 bg-gradient-to-r from-orange-400 to-pink-400 text-white hover:opacity-90 hover:scale-105"
            >
              Upgrade to Pro
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 px-6 rounded-lg font-medium transition-colors text-white/70 hover:text-white hover:bg-white/5"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
